import type { Account, PositionAssetClass, Transaction } from '@fleece/models';
import { Decimal, NotFoundError } from '@fleece/utilities';
import { ArrowLeft, ChevronLeft, ChevronRight, ListFilter, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { AccountStatus, AccountTypeBadge, Pnl, RefreshButton, Stat, SymbolCell } from '@/components/ledger-parts';
import { OrderEventsDialog } from '@/components/order-events-dialog';
import { Rows } from '@/components/settings/table-parts';
import { Button, Message, Skeleton, Spinner, Switch, Table, TableCard, Td, Th } from '@/components/ui';
import { useAccount, useLatestTransaction, usePositions, useProfits, useTransactions } from '@/hooks/use-ledger';
import { easternDate, easternTime, NA } from '@/lib/format';
import {
  describeTrade,
  money,
  pageNumbers,
  pagesWithRows,
  positionRows,
  quantity,
  quote,
  realisedTotal,
  roiPercent,
  symbolLabel,
  transactionKey,
  type PositionRow,
  type TransactionPage,
} from '@/lib/ledger';
import { routeInfo } from '@/lib/route';
import { cn } from '@/lib/utils';

interface SymbolFilter {
  readonly symbol: string;
  readonly assetClass: PositionAssetClass;
}

/**
 * One virtual account: what it holds now, what it has realised, and the log of every fill
 * booked against it. Picking a position narrows the log to that symbol.
 *
 * Read-only. Figures are the ledger's, exactly: cost basis, never a mark — the ledger holds
 * no market price, and an unrealised P&L made up from one fetched here would be a number
 * nothing else in Fleece agrees with.
 */
export function AccountPage({ accountId }: { accountId: string }) {
  const account = useAccount(accountId);
  const [filter, setFilter] = useState<SymbolFilter | undefined>();

  // Only before the account has loaded: a refresh that fails later keeps the page, its
  // tables and the page of the log being read, rather than trading them for an error with
  // no way to try again.
  if (account.data === undefined && account.error !== null) {
    return (
      <Page>
        <BackLink />
        <Message tone="error">
          {account.error instanceof NotFoundError ? `There is no account ${accountId}. It may have been deleted, or the link mistyped.` : account.error.message}
        </Message>
      </Page>
    );
  }

  return (
    <Page>
      <BackLink />
      <AccountHeader account={account.data?.account} accountId={accountId} />
      <Headline accountId={accountId} />
      <PositionsCard accountId={accountId} filter={filter} onFilter={setFilter} />
      {/* Keyed by symbol, so narrowing the log starts it again at its first page. */}
      <TransactionsCard key={filter?.symbol ?? ''} accountId={accountId} filter={filter} onClearFilter={() => setFilter(undefined)} />
    </Page>
  );
}

function Page({ children }: { children: ReactNode }) {
  return <div className="mx-auto flex max-w-[1100px] flex-col gap-3 px-2 py-3 pb-6 sm:px-3">{children}</div>;
}

function BackLink() {
  const accounts = routeInfo('accounts');
  return (
    <a href={accounts.hash} className="flex w-fit items-center gap-1 text-xs text-ink-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-accent">
      <ArrowLeft className="size-3.5" /> {accounts.label}
    </a>
  );
}

function AccountHeader({ account, accountId }: { account: Account | undefined; accountId: string }) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-2 px-0.5">
      <div className="flex min-w-0 flex-col gap-1">
        {account === undefined ? (
          <Skeleton className="h-5 w-48" />
        ) : (
          <h1 className="flex flex-wrap items-center gap-2 text-base font-semibold tracking-tight">
            {account.name}
            <AccountTypeBadge type={account.accountType} />
            <AccountStatus status={account.status} />
          </h1>
        )}
        <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted">
          <span className="font-mono">{accountId}</span>
          {account !== undefined && <span>· created {easternDate(account.createdAt)}</span>}
        </p>
      </div>
      <RefreshButton />
    </header>
  );
}

function Headline({ accountId }: { accountId: string }) {
  const profits = useProfits(accountId);
  const positions = usePositions(accountId, false);
  const latest = useLatestTransaction(accountId);
  // Cash is a position in the ledger, but not one anybody means by "open positions".
  const held = positions.data?.positions ?? [];
  const open = held.filter((position) => position.assetClass !== 'cash');
  const cash = held.find((position) => position.assetClass === 'cash');
  const shorts = open.filter((position) => position.size.isNegative()).length;
  const last = latest.data?.transactions[0];

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
      <Stat label="Cash" detail="paid in and out by its fills">
        {positions.data === undefined ? <StatPlaceholder error={positions.error} /> : <span className="tabular">{money(cash?.size ?? Decimal.ZERO)}</span>}
      </Stat>
      <Stat
        label="Realised P&L"
        detail={profits.data === undefined ? undefined : `across ${profits.data.profits.length} ${profits.data.profits.length === 1 ? 'symbol' : 'symbols'}`}
      >
        {profits.data === undefined ? <StatPlaceholder error={profits.error} /> : <Pnl value={realisedTotal(profits.data.profits)} />}
      </Stat>
      <Stat label="Open positions" detail={positions.data === undefined ? undefined : `${open.length - shorts} long · ${shorts} short`}>
        {positions.data === undefined ? <StatPlaceholder error={positions.error} /> : open.length}
      </Stat>
      <Stat label="Last trade" detail={last === undefined ? undefined : `${easternTime(last.timestamp)} ET · ${symbolLabel(last.symbol, last.assetClass)}`}>
        {latest.data === undefined ? <StatPlaceholder error={latest.error} /> : last === undefined ? 'Never' : easternDate(last.timestamp)}
      </Stat>
    </div>
  );
}

function StatPlaceholder({ error }: { error: Error | null }) {
  return error === null ? (
    <Skeleton className="my-0.5 h-5 w-20" />
  ) : (
    <span className="text-negative" title={error.message}>
      {NA}
    </span>
  );
}

function PositionsCard({ accountId, filter, onFilter }: { accountId: string; filter: SymbolFilter | undefined; onFilter: (filter: SymbolFilter | undefined) => void }) {
  const [includeClosed, setIncludeClosed] = useState(false);
  const positions = usePositions(accountId, includeClosed);
  const profits = useProfits(accountId);
  // Cash has its own stat above; as a row it would keep "No open positions" from ever
  // showing once the account has traded.
  const instruments = (positions.data?.positions ?? []).filter((position) => position.assetClass !== 'cash');
  const rows = positionRows(instruments, profits.data?.profits ?? []);

  return (
    <TableCard
      title="Positions"
      count={positions.data === undefined ? undefined : rows.length}
      action={<Switch checked={includeClosed} onChange={setIncludeClosed} label="Show closed" />}
    >
      <Rows
        query={positions}
        count={rows.length}
        empty={includeClosed ? 'Nothing traded in this account yet.' : 'No open positions. Show closed ones to see what was traded before.'}
      >
        <Table>
          <thead>
            <tr>
              <Th>Symbol</Th>
              <Th className="hidden md:table-cell">Class</Th>
              <Th className="text-right">Size</Th>
              <Th className="hidden text-right sm:table-cell">
                <span title="As a broker quotes it: per share, so an option's is its premium.">Avg price</span>
              </Th>
              <Th className="text-right">
                <span title="The dollars behind the position: negative for a short, which is the credit received.">Cost basis</span>
              </Th>
              <Th className="text-right">Realised</Th>
              <Th className="hidden lg:table-cell">Updated</Th>
              <Th>
                <span className="sr-only">Transactions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <PositionTableRow key={row.position.symbol} row={row} selected={filter?.symbol === row.position.symbol} onFilter={onFilter} />
            ))}
          </tbody>
        </Table>
      </Rows>
    </TableCard>
  );
}

function PositionTableRow({ row, selected, onFilter }: { row: PositionRow; selected: boolean; onFilter: (filter: SymbolFilter | undefined) => void }) {
  const { position, realised } = row;
  const closed = position.size.isZero();
  const isOption = position.assetClass === 'option';
  return (
    <tr className={cn(selected && 'bg-accent-soft/60', closed && 'text-ink-2')}>
      <Td>
        <SymbolCell symbol={position.symbol} assetClass={position.assetClass} />
      </Td>
      <Td className="hidden text-ink-2 capitalize md:table-cell">{position.assetClass}</Td>
      <Td className="text-right tabular" title={position.size.toString()}>
        {closed ? <span className="text-muted">Closed</span> : quantity(position.size)}
      </Td>
      <Td
        className="hidden text-right tabular sm:table-cell"
        title={isOption ? `${position.premium.toString()} a share, ${position.avgPrice.toString()} a contract` : position.avgPrice.toString()}
      >
        {closed ? NA : quote(position.premium)}
      </Td>
      <Td className="text-right tabular" title={position.totalCost.toString()}>
        {closed ? NA : money(position.totalCost)}
      </Td>
      <Td className="text-right">
        <Pnl value={realised} />
      </Td>
      <Td className="hidden text-ink-2 tabular lg:table-cell">{easternDate(position.lastUpdatedAt)}</Td>
      <Td className="w-8 pr-1.5">
        <Button
          variant="ghost"
          onClick={() => onFilter(selected ? undefined : { symbol: position.symbol, assetClass: position.assetClass })}
          aria-label={`Show only the transactions in ${position.symbol}`}
          aria-pressed={selected}
          title={selected ? 'Show every symbol again' : 'Show only its transactions'}
          className={cn(selected && 'text-accent')}
        >
          <ListFilter className="size-3.5" />
        </Button>
      </Td>
    </tr>
  );
}

/**
 * The log a page at a time, newest first. The pager in the header numbers the pages fetched
 * so far: Next moves one page on, fetching it if it has not been, and Load more fetches the
 * page after the furthest reached and moves to it — or to the last page, once there are no
 * more.
 */
function TransactionsCard({ accountId, filter, onClearFilter }: { accountId: string; filter: SymbolFilter | undefined; onClearFilter: () => void }) {
  const transactions = useTransactions(accountId, filter?.symbol);
  const [requested, setRequested] = useState(0);
  const [inspecting, setInspecting] = useState<Transaction | undefined>();
  // A log that ends exactly on a page boundary answers the next request with nothing; that
  // empty page is not one to number.
  const pages = pagesWithRows(transactions.data?.pages);
  // A refresh can return fewer pages than were fetched, if rows went away; show what is left.
  const current = Math.max(0, Math.min(requested, pages.length - 1));
  const rows = pages[current]?.transactions ?? [];
  const sizes = pages.map((page) => page.transactions.length);
  const before = sizes.slice(0, current).reduce((sum, size) => sum + size, 0);
  // Known only once the last page has been fetched: nothing else says how long the log is.
  const total = transactions.hasNextPage ? undefined : sizes.reduce((sum, size) => sum + size, 0);
  const lastPage = pages.length - 1;
  // Any fetch, not only the next page's: asking for a page while a refresh reads the
  // others again would cancel the refresh, and the newest fills would never arrive.
  const busy = transactions.isFetching;

  /** Fetches the page after the furthest reached; the pages there are afterwards, or undefined if the fetch failed. */
  const fetchMore = async (): Promise<ReadonlyArray<TransactionPage> | undefined> => {
    const result = await transactions.fetchNextPage();
    return result.isFetchNextPageError ? undefined : pagesWithRows(result.data?.pages);
  };

  /** Moves to `target` once a fetch lands — unless another page was picked while it was out. */
  const moveAfterFetch = (from: number, target: number): void => setRequested((now) => (now === from ? target : now));

  const next = async (): Promise<void> => {
    if (current < lastPage) {
      setRequested(current + 1);
      return;
    }
    const fetched = await fetchMore();
    if (fetched !== undefined && fetched.length > current + 1) {
      moveAfterFetch(requested, current + 1);
    }
  };

  const loadMore = async (): Promise<void> => {
    if (!transactions.hasNextPage) {
      setRequested(lastPage);
      return;
    }
    const fetched = await fetchMore();
    if (fetched !== undefined) {
      // The page just fetched — or, when it came back empty because the log ended on a page
      // boundary, the last page there is.
      moveAfterFetch(requested, fetched.length - 1);
    }
  };

  const pager =
    pages.length > 1 || transactions.hasNextPage ? (
      <Pager
        count={pages.length}
        current={current}
        canAdvance={current < lastPage || transactions.hasNextPage}
        busy={busy}
        onPage={setRequested}
        onPrevious={() => setRequested(current - 1)}
        onNext={() => void next()}
        onLoadMore={() => void loadMore()}
      />
    ) : undefined;

  return (
    <TableCard
      title="Transactions"
      action={
        filter === undefined && pager === undefined ? undefined : (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {filter !== undefined && <FilterChip filter={filter} onClear={onClearFilter} />}
            {pager}
          </div>
        )
      }
      footer={
        rows.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-2.5 py-2 text-xs text-muted">
            <span className="tabular">
              {before + 1}–{before + rows.length}
              {total === undefined ? '' : ` of ${total}`} · newest first
            </span>
            {transactions.isFetchNextPageError && (
              <span role="alert" className="text-negative">
                {transactions.error?.message}
              </span>
            )}
          </div>
        )
      }
    >
      {/* An error once rows are shown is a page that failed to load, said in the footer; the rows already read stay. */}
      <Rows
        query={{ isPending: transactions.isPending, error: transactions.data === undefined ? transactions.error : null }}
        count={rows.length}
        empty={filter === undefined ? 'No transactions in this account yet.' : `No transactions in ${filter.symbol}.`}
      >
        <Table>
          <thead>
            <tr>
              <Th>Time (ET)</Th>
              <Th>Symbol</Th>
              <Th>Trade</Th>
              <Th className="text-right">Size</Th>
              <Th className="hidden text-right sm:table-cell">Price</Th>
              <Th className="hidden text-right sm:table-cell">Amount</Th>
              <Th className="text-right">Realised</Th>
              <Th className="hidden text-right md:table-cell">Position after</Th>
              <Th className="hidden md:table-cell">Order</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((transaction) => (
              <TransactionRow key={transactionKey(transaction)} transaction={transaction} onInspect={() => setInspecting(transaction)} />
            ))}
          </tbody>
        </Table>
      </Rows>
      <OrderEventsDialog transaction={inspecting} onClose={() => setInspecting(undefined)} />
    </TableCard>
  );
}

function Pager({
  count,
  current,
  canAdvance,
  busy,
  onPage,
  onPrevious,
  onNext,
  onLoadMore,
}: {
  count: number;
  current: number;
  /** Both Next and Load more have somewhere to go: a page after this one, fetched or not. */
  canAdvance: boolean;
  busy: boolean;
  onPage: (index: number) => void;
  onPrevious: () => void;
  onNext: () => void;
  onLoadMore: () => void;
}) {
  return (
    <nav aria-label="Transaction pages" className="flex items-center gap-0.5">
      <Button variant="ghost" onClick={onPrevious} disabled={current === 0 || busy} aria-label="Previous page" title="Previous page" className="px-1.5">
        <ChevronLeft className="size-3.5" />
      </Button>
      {pageNumbers(count, current).map((item, position) => {
        if (item === 'gap') {
          return (
            <span key={`gap-${position}`} aria-hidden className="px-1 text-xs text-muted">
              …
            </span>
          );
        }
        const shown = item === current;
        return (
          <Button
            key={item}
            variant="ghost"
            onClick={() => onPage(item)}
            disabled={busy}
            aria-current={shown ? 'page' : undefined}
            aria-label={`Page ${item + 1}`}
            className={cn('min-w-7 px-1.5 tabular', shown && 'bg-accent-soft text-accent hover:bg-accent-soft hover:text-accent')}
          >
            {item + 1}
          </Button>
        );
      })}
      <Button variant="ghost" onClick={onNext} disabled={!canAdvance || busy} aria-label="Next page" title="Next page" className="px-1.5">
        <ChevronRight className="size-3.5" />
      </Button>
      <Button variant="outline" onClick={onLoadMore} disabled={!canAdvance || busy} className="ml-1" title="Fetch the page after the last one and show it">
        {busy && <Spinner />} Load more
      </Button>
    </nav>
  );
}

function FilterChip({ filter, onClear }: { filter: SymbolFilter; onClear: () => void }) {
  const label = symbolLabel(filter.symbol, filter.assetClass);
  return (
    <Button variant="outline" onClick={onClear} aria-label={`Show every symbol, not only ${filter.symbol}`} title={filter.symbol}>
      <ListFilter className="size-3.5 text-accent" />
      <span className="max-w-[14rem] truncate">{label}</span>
      <X className="size-3.5" />
    </Button>
  );
}

function TransactionRow({ transaction, onInspect }: { transaction: Transaction; onInspect: () => void }) {
  const trade = describeTrade(transaction);
  const isOption = transaction.assetClass === 'option';
  return (
    <tr>
      <Td className="tabular whitespace-nowrap" title={new Date(transaction.timestamp).toISOString()}>
        <span className="flex flex-col gap-0.5">
          <span>{easternDate(transaction.timestamp)}</span>
          <span className="text-[11px] text-muted">{easternTime(transaction.timestamp)}</span>
        </span>
      </Td>
      <Td>
        <SymbolCell symbol={transaction.symbol} assetClass={transaction.assetClass} />
      </Td>
      <Td className="whitespace-nowrap">
        {transaction.assetClass === 'cash' ? (
          <span className="font-medium">{transaction.size.isNegative() ? 'Paid out' : 'Paid in'}</span>
        ) : (
          <>
            <span className="font-medium capitalize">{trade.side}</span>
            <span className="text-muted"> · {trade.effect}</span>
          </>
        )}
      </Td>
      <Td className="text-right tabular" title={transaction.size.toString()}>
        {quantity(transaction.size.abs())}
      </Td>
      <Td
        className="hidden text-right tabular sm:table-cell"
        title={isOption ? `${transaction.premium.toString()} a share, ${transaction.avgPrice.toString()} a contract` : transaction.avgPrice.toString()}
      >
        {quote(transaction.premium)}
      </Td>
      <Td className="hidden text-right tabular sm:table-cell" title={transaction.totalCost.toString()}>
        {money(transaction.totalCost.abs())}
      </Td>
      <Td className="text-right">
        {transaction.profit === undefined ? (
          <span className="text-muted">{NA}</span>
        ) : (
          <span className="flex flex-col items-end gap-0.5">
            <Pnl value={transaction.profit} />
            <span className="text-[11px] text-muted tabular">{roiPercent(transaction.roi)}</span>
          </span>
        )}
      </Td>
      <Td className="hidden text-right text-ink-2 tabular md:table-cell" title={transaction.cumulativeSize.toString()}>
        {quantity(transaction.cumulativeSize)}
      </Td>
      <Td className="hidden max-w-[8rem] md:table-cell lg:max-w-[10rem]">
        {transaction.referenceId === undefined ? (
          <span className="text-muted" title={`Event record ${transaction.recordId}: not a broker's fill, so there is no order to show`}>
            {NA}
          </span>
        ) : (
          <button
            type="button"
            onClick={onInspect}
            title={`Show the broker's events for order ${transaction.referenceId}`}
            className="block w-full truncate text-left font-mono text-[11px] text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
          >
            {transaction.referenceId}
          </button>
        )}
      </Td>
    </tr>
  );
}
